import React, { Component, ReactElement } from 'react'
import gql from 'graphql-tag'
import { ApolloConsumer } from 'react-apollo'
import { ApolloClient } from 'apollo-client'

interface Job {
  readonly id: string
  readonly total: number
  readonly progress: number
}

interface RendererProps {
  readonly trackJob: (job: Job) => Promise<void>
  readonly trackedJob?: Job
}

interface Props {
  readonly children: (props: RendererProps) => ReactElement<any>
}

interface State {
  readonly trackedJob?: Job
  readonly interval: number
}

export const jobQuery = gql`
  query jobStatus($id: [ID!]!) {
    jobStatus(id: $id) {
      id
      total
      progress
    }
  }
`

interface QueryResult {
  readonly jobStatus: Job
}

export class JobTracker extends Component<Props, State> {
  public readonly state: State = {
    trackedJob: undefined,
    interval: 0
  }

  private updateTimer: any
  private clearJobTimer: any
  private apollo: ApolloClient<any> | undefined
  private jobResolver: () => void

  public render() {
    return (
      <ApolloConsumer>
        {(client) => {
          this.apollo = client

          return this.props.children({
            trackJob: this.trackJob,
            trackedJob: this.state.trackedJob
          })
        }}
      </ApolloConsumer>
    )
  }

  public componentDidUpdate() {
    const { trackedJob } = this.state

    // if the job is still running and we're not already fetching an update,
    // schedule the tracked job status for an update
    if (trackedJob && jobIsRunning(trackedJob) && !this.updateTimer) {
      this.updateTimer = setTimeout(async () => {
        this.updateTimer = undefined
        if (this.apollo) {
          const result = await this.apollo.query<QueryResult>({
            query: jobQuery,
            variables: { id: trackedJob.id },
            fetchPolicy: 'network-only'
          })
          if (!result.errors) {
            this.setState({ trackedJob: result.data.jobStatus })
          }
        }
      }, 250)
    }
    // if the job is not running, but hasn't been cleared yet, set the job to
    // be cleared after a small amount of time. This makes sure that any
    // component rendered based on the job being active is visible long enough
    // to see the completed status
    else if (trackedJob && !jobIsRunning(trackedJob) && !this.clearJobTimer) {
      this.clearJobTimer = setTimeout(() => {
        this.clearJobTimer = undefined
        this.setState({ trackedJob: undefined }, this.jobResolver)
      }, 500)
    }
  }

  public componentWillUnmount() {
    if (this.updateTimer) {
      clearTimeout(this.updateTimer)
    }

    if (this.clearJobTimer) {
      clearTimeout(this.clearJobTimer)
    }
  }

  private trackJob = (trackedJob: Job): Promise<void> => {
    if (!this.state.trackedJob) {
      this.setState({ trackedJob })
      return new Promise((resolve) => (this.jobResolver = resolve))
    }

    return Promise.resolve()
  }
}

function jobIsRunning(job: Job) {
  return job.total - job.progress > 0
}
